import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';

import App from './App.tsx';
import './index.css';
import './utils/cleanAuth'; // Importer l'utilitaire de nettoyage
import './utils/consoleOverride';
import { ErrorBoundary } from './components/system/ErrorBoundary';

// Global error handler pour capturer les erreurs de variables non définies
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message && event.message.includes('profile is not defined')) {
      console.error('🚨 [Global Error Handler] profile is not defined error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
      // Ne pas empêcher le comportement par défaut, mais logger pour debug
    }
  });

  // Aussi capturer les erreurs non gérées dans les promesses
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && String(event.reason).includes('profile is not defined')) {
      console.error('🚨 [Global Error Handler] Unhandled promise rejection with profile error:', event.reason);
    }
  });
}

/** Initialisation du thème - sera géré par ThemeProvider */
try {
  // Ne pas forcer le thème, laisser ThemeProvider gérer cela
} catch {}

/** Initialisation du système de console override */
// Le système de console override s'initialise automatiquement

/** Pare-chocs global pour éviter l'écran blanc en cas d'erreur non rattrapée */
class GlobalErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error?: any; errorInfo?: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: undefined, errorInfo: undefined };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error('[GlobalErrorBoundary] Error caught:', error);
    console.error('[GlobalErrorBoundary] Error info:', errorInfo);
    this.setState({ errorInfo });
    
    // En production, envoyer l'erreur à un service de tracking si nécessaire
    if (import.meta.env.PROD) {
      // Vous pouvez ajouter un service de tracking ici
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--card))] p-6">
          <div className="max-w-md w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-6 text-center">
            <h1 className="text-lg font-semibold text-[rgb(var(--fg))] mb-2">Oops, an error occurred</h1>
            <p className="text-sm text-[rgb(var(--muted))] mb-4">
              The application encountered a problem. Try refreshing the page. If this persists, please sign in again.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="text-xs text-[rgb(var(--muted))] cursor-pointer mb-2">Error details (dev only)</summary>
                <pre className="text-xs text-red-400 bg-slate-800 p-2 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack && (
                    <div className="mt-2 text-gray-500">
                      {this.state.errorInfo.componentStack}
                    </div>
                  )}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: undefined, errorInfo: undefined });
                location.reload();
              }}
              className="px-4 py-2 rounded-lg bg-slate-700 text-[rgb(var(--fg))] hover:bg-slate-600 mt-4"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Vérifier que l'élément root existe avant de rendre
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Make sure index.html has a <div id="root"></div> element.');
}

try {
  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary>
        <GlobalErrorBoundary>
          <App />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1f2937', // gris foncé
                color: '#f9fafb', // texte clair
                border: '1px solid #374151',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
              },
              success: {
                duration: 3000,
                style: {
                  background: '#064e3b',
                  color: '#d1fae5',
                  border: '1px solid #059669',
                },
                iconTheme: {
                  primary: '#39d353',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                style: {
                  background: '#7f1d1d',
                  color: '#fecaca',
                  border: '1px solid #dc2626',
                },
                iconTheme: {
                  primary: '#ff6b6b',
                  secondary: '#fff',
                },
              },
              loading: {
                style: {
                  background: '#1f2937',
                  color: '#f9fafb',
                  border: '1px solid #374151',
                },
                iconTheme: {
                  primary: '#4a9eff',
                  secondary: '#fff',
                },
              },
            }}
          />
        </GlobalErrorBoundary>
      </ErrorBoundary>
    </StrictMode>
  );
      } catch (error) {
  console.error('[main.tsx] Failed to render app:', error);
  // Afficher un message d'erreur dans le DOM si le rendu échoue
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #0f172a; color: white; padding: 2rem; text-align: center;">
        <div>
          <h1 style="font-size: 1.5rem; margin-bottom: 1rem;">Failed to load application</h1>
          <p style="color: #94a3b8; margin-bottom: 1rem;">An error occurred while loading the app. Please refresh the page.</p>
          <button onclick="location.reload()" style="padding: 0.5rem 1rem; background: #1e293b; color: white; border: 1px solid #334155; border-radius: 0.5rem; cursor: pointer;">
            Refresh Page
          </button>
          ${import.meta.env.DEV ? `<pre style="margin-top: 1rem; text-align: left; font-size: 0.75rem; color: #ef4444; background: #1e293b; padding: 1rem; border-radius: 0.5rem; overflow: auto;">${error}</pre>` : ''}
        </div>
      </div>
    `;
  }
}
